import { Button, Text } from '@chakra-ui/react';
import { toast } from 'react-toastify';

import { User, UserType, useUpdateUserSettingsMutation } from '../../../gql/generated/graphql';

import Modal, { LEModalProps } from '../../../components/shared/Modal';

import {
  DEFAULT_ASSIGNMENT_MODE,
  DEFAULT_FREQUENCY_MINUTES,
  DEFAULT_USER_TYPE,
  MIN_MAX_AUTO_ASSIGN_DEALS,
} from '../../../constants/user';
import { logger } from '../../../libs/Logger';

type UpdateUserVacationModalProps = LEModalProps & {
  user: User | null;
};

const UpdateUserVacationModal = ({
  user,
  isOpen,
  onClose,
  ...rest
}: UpdateUserVacationModalProps) => {
  const [updateUserSettings, { loading }] = useUpdateUserSettingsMutation();

  if (!user || !user.id || !user.hours || user.hours == null || user.auto_assign_deals == null) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Update Vacation"
      leftButtons={
        <Button variant="warning" onClick={onClose}>
          Cancel
        </Button>
      }
      rightButtons={
        <Button
          isLoading={loading}
          onClick={async () => {
            try {
              await updateUserSettings({
                variables: {
                  id: user.id ?? '',
                  hours: user.hours ?? '',
                  on_vacation: !user.on_vacation,
                  auto_assign_deals: !!user.auto_assign_deals,
                  max_auto_assign_deals: user.max_auto_assign_deals ?? MIN_MAX_AUTO_ASSIGN_DEALS,
                  user_type: user.user_type ?? DEFAULT_USER_TYPE,
                  assignment_mode: user.assignment_mode ?? DEFAULT_ASSIGNMENT_MODE,
                  frequency_minutes: user.frequency_minutes ?? DEFAULT_FREQUENCY_MINUTES,
                  accept_overnight_deals:
                    user.autoAssignEligibility?.accept_overnight_deals ?? false,
                  accept_unseen_deals: user.autoAssignEligibility?.accept_unseen_deals ?? false,
                  accept_with_upcoming_appointment:
                    user.autoAssignEligibility?.accept_with_upcoming_appointment ?? false,
                  accept_ai_touched_deals:
                    user.autoAssignEligibility?.accept_ai_touched_deals ??
                    (user.user_type ?? DEFAULT_USER_TYPE) !== UserType.Ai,
                  deals_with_stipulations:
                    user.autoAssignEligibility?.deals_with_stipulations ?? true,
                  deals_with_vsc: user.autoAssignEligibility?.deals_with_vsc ?? true,
                  deals_with_gap: user.autoAssignEligibility?.deals_with_gap ?? true,
                  deals_with_double_tax: user.autoAssignEligibility?.deals_with_double_tax ?? true,
                  deals_with_direct_pay: user.autoAssignEligibility?.deals_with_direct_pay ?? true,
                  deals_with_bank_fees: user.autoAssignEligibility?.deals_with_bank_fees ?? true,
                  deals_with_down_payment:
                    user.autoAssignEligibility?.deals_with_down_payment ?? true,
                },
              });
              toast.success('User updated successfully.');
              onClose();
            } catch (e) {
              toast.error('Failed to update user.');
              logger.error(
                'UpdateUserVacationModal',
                'Failed to update vacation setting',
                {
                  id: user?.id,
                  vacation: user?.on_vacation,
                },
                e,
              );
            }
          }}
        >
          Confirm
        </Button>
      }
      {...rest}
    >
      <Text>Are you sure you want to update the vacation setting for {user?.name}</Text>
    </Modal>
  );
};

export default UpdateUserVacationModal;
